import React, { useMemo, useState } from 'react';
import WithSection from '../../components/WithSection/WithSection';
import { graphql } from 'gatsby';
import Box from '../../components/Box/Box';
import { Body1, Head2, Head6 } from '../../components/Typography/Typography';
import Image from '../../components/Image/Image';
import { isLightColor } from '../../utils/isLightColor';
import { rem } from 'polished';
import Stack from '../../components/Stack/Stack';
import InputSlots, { normalizeValues } from '../../slots/InputSlots/InputSlots';
import { useForm } from 'react-hook-form';
import { submitForm } from '../../services/externalForm';
import Button from '../../components/Button/Button';
import { track } from '../../utils/analytics';
import { VideoThumbnail } from '../../components/VideoThumbnail/VideoThumbnail';
import RichText from '../../components/RichText/RichText';
import { navigate } from 'gatsby';
import { success } from '../../../config/colors/alert';

/*
 * Check Jira for the business requirements to understand the business needs:
 * https://adamdotai.atlassian.net/jira/software/c/projects/GP/boards/26?selectedIssue=GP-106
 * */

const formWrapperStyles = {
    alignSelf: 'start',
    bg: 'neutral.white',
    gridColumn: {
        _: '1/-1',
        sm: '5 / span 4',
        md: '6 / span 4',
        lg: '8 / span 4',
        '2xl': '8 / span 4',
    },
    borderRadius: 'sm',
    p: rem(32),
};

const rightContentGridColumns = {
    _: '1/-1',
    sm: '1 / span 4',
    md: '1 / span 4',
    lg: '2 / span 5',
};

const RequestDemoHeroSection = ({
    data,
    styling: { backgroundVariant: sectionBackground },
}) => {
    const {
        primary: {
            form,
            section_title,
            description,
            image,
            badges_image,
            video_embed_link,
            inline_video_mp4,
            inline_video_webm,
        },
    } = data;
    const {
        submit_url,
        cta_text,
        cta_variant,
        fields,
        cta_tracking_event,
        success_msg,
        success_image,
    } = form.document.data;
    const isDarkBackground = !isLightColor(sectionBackground);
    const { handleSubmit, register, errors, control, setError } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const onSubmit = (values) => {
        setIsSubmitting(true);
        const countryName = values.country.label;

        const gccCountries = [
            'Bahrain',
            'Kuwait',
            'Oman',
            'Qatar',
            'Saudi Arabia',
            'United Arab Emirates',
            'Djibouti',
            'Egypt',
            'Iran',
            'Iraq',
            'Jordan',
            'Lebanon',
            'Libya',
            'Morocco',
            'Syria',
            'Tunisia',
            'Palestine',
            'Yemen',
        ];

        const usaAndCanadaCountries = [
            'United States',
            'Canada',
            'Mexico',
            'Brazil',
            'Colombia',
            'Argentina',
            'Bolivia',
            'Uruguay',
            'Chile',
            'Venezuela',
            'Panama',
            'Ecuador',
            'Paraguay',
            'Honduras',
            'Peru',
            'Guatemala',
            'Nicaragua',
            'Costa Rica',
            'El Salvador',
            'Dominican Republic',
            'Cuba',
            'Haiti',
            'Puerto Rico',
            'French Guiana',
            'Martinique',
            'Guadeloupe',
            'Saint Lucia',
            'Antigua and Barbuda',
            'Saint Martin',
            'Saint Barthélemy',
            'Belize',
        ];

        const BookingConditions = {
            USA_CANADA: usaAndCanadaCountries.includes(countryName),
            GCC: gccCountries.includes(countryName),
            ALL_EXCEPT_GCC_USA_CANADA:
                !gccCountries.includes(countryName) &&
                !usaAndCanadaCountries.includes(countryName),
        };

        const normalizedValues = normalizeValues(values);
        submitForm({ url: submit_url, data: normalizedValues })
            .then((res) => {
                track(cta_tracking_event, normalizedValues);
                setIsSubmitted(true);
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            <Stack
                space={rem(16)}
                gridColumn={rightContentGridColumns}
                marginBottom={{ _: rem(60), md: '0' }}
            >
                <Stack
                    space={rem(8)}
                    maxWidth={{ _: 'initial', md: rem(523) }}
                    textAlign={{ _: 'center', md: 'initial' }}
                >
                    <Head2
                        py={rem(8)}
                        {...(isDarkBackground && {
                            color: 'neutral.white',
                        })}
                    >
                        {section_title.text}
                    </Head2>
                    <Body1
                        py={rem(8)}
                        {...(isDarkBackground && {
                            color: 'neutral.white',
                        })}
                    >
                        <RichText render={description.richText} />
                    </Body1>
                </Stack>
                <Box display="flex" justifyContent="center">
                    <VideoThumbnail
                        video_embed_link={video_embed_link}
                        image={image}
                        playIconSize={90}
                        inlineVideoMp4={inline_video_mp4.url}
                        inlineVideoWebm={inline_video_webm.url}
                    />
                </Box>
                <Box display="flex" justifyContent="center">
                    <Image img={badges_image} />
                </Box>
            </Stack>

            {isSubmitted ? (
                <Stack space={rem(20)} {...formWrapperStyles}>
                    <Box
                        textAlign="center"
                        bg="neutral.white"
                        borderRadius="sm"
                        overflow="hidden"
                    >
                        <Image
                            img={success_image}
                            // @ts-ignore
                            backgroundColor="neutral.white"
                        />
                    </Box>
                    {/*<Head6>*/}
                    {/*    {thank_you_box_title}, {first_name}*/}
                    {/*</Head6>*/}
                    <Body1>{success_msg}</Body1>
                </Stack>
            ) : (
                <Stack
                    as="form"
                    onSubmit={handleSubmit(onSubmit)}
                    space={rem(20)}
                    noValidate
                    {...formWrapperStyles}
                >
                    <InputSlots
                        inputs={fields}
                        excludePersonalEmailDomains
                        extendProps={({
                            key,
                            is_required,
                            pattern,
                            label,
                        }) => ({
                            ref: register({
                                required: is_required,
                                pattern,
                            }),
                            error: errors[key],
                            control,
                            label,
                        })}
                    />
                    <Box py={rem(8)}>
                        <Button
                            size="lg"
                            fullWidth
                            loading={isSubmitting}
                            variant={cta_variant}
                        >
                            {cta_text}
                        </Button>
                    </Box>
                </Stack>
            )}
        </>
    );
};

export default WithSection(RequestDemoHeroSection);

export const _ = graphql`
    fragment REQUEST_DEMO_HERO_SECTION on PrismicFormDefault {
        id
        primary {
            section_title: title {
                text
            }
            description {
                richText
            }
            image {
                gatsbyImageData(layout: CONSTRAINED, height: 400)
                alt
                url
            }
            badges_image {
                gatsbyImageData(layout: CONSTRAINED, height: 170)
                alt
                url
            }
            video_embed_link {
                url
            }
            inline_video_mp4 {
                ...PRISMIC_LINK_FRAGMENT
            }
            inline_video_webm {
                ...PRISMIC_LINK_FRAGMENT
            }
            form {
                type
                id
                document {
                    ... on PrismicDynamicForm {
                        id
                        data {
                            cta_text
                            cta_variant
                            cta_tracking_event
                            fields {
                                key
                                is_required
                                label
                                options
                                placeholder
                                type
                            }
                            submit_url
                            success_msg
                            success_image {
                                gatsbyImageData(
                                    height: 305
                                    layout: CONSTRAINED
                                )
                            }
                        }
                    }
                }
            }
        }
    }
`;
